import type { ResponsiveValue } from '@shopify/restyle/dist/types';
import { LogoSerresTessierHorizontal } from 'app/design/branding/logo';
import { Column } from 'app/design/primitives/box';
import { Input } from 'app/design/primitives/input';
import { Link } from 'app/design/primitives/link';
import { Text } from 'app/design/primitives/text';
import { useNavigation, useNavigationOptions } from 'app/hooks/navigation';
import { safelyOpenURL } from 'app/lib/navigation';
import type { CredentialsFormSchema } from 'app/screens/onboarding/hooks/use-credentials-form';
import type { Theme } from 'app/shopify';
import { createTheme as createDSTheme } from 'app/shopify';
import { isWhiteLabel } from 'app/utils/helpers';
import type { TFunction } from 'i18next';
import React from 'react';
import { Controller, type Control } from 'react-hook-form';
import { Trans } from 'react-i18next';
import type { TextInput } from 'react-native';
import { Pressable, View } from 'react-native';
const theme = createDSTheme();

export type OnboardingNavigationHeaderPresentation = 'overlay' | 'solid';

export const useOnboardingNavigation = (
  userType?: string | null,
  opts?: { headerPresentation?: OnboardingNavigationHeaderPresentation },
) => {
  const navigation = useNavigation<'onboarding'>();
  const headerBackgroundColor =
    userType === 'public' ? theme.colors['surface.brand.secondary-3.default'] : theme.colors['white-label.header.bg'];
  const headerForegroundColor =
    userType === 'public' ? theme.colors['text.secondary-3.strong'] : theme.colors['text.primary'];
  const progressActiveColor: ResponsiveValue<keyof Theme['colors'], Theme['breakpoints']> =
    userType === 'public' ? 'surface.brand.secondary-3.default' : 'surface.brand.default';

  const presentation = opts?.headerPresentation ?? 'solid';
  const overlayLogoColor = theme.colors['surface.default'];

  useNavigationOptions(
    navigation,
    presentation === 'overlay'
      ? {
          headerTintColor: overlayLogoColor,
          headerTransparent: true,
          ...(userType === 'public'
            ? {
                headerTitle: () => (
                  <View style={{ marginTop: 5 }}>
                    <LogoSerresTessierHorizontal color={overlayLogoColor} height={22} width={180} />
                  </View>
                ),
              }
            : {}),
        }
      : {
          headerStyle: { backgroundColor: headerBackgroundColor },
          headerTintColor: headerForegroundColor,
          ...(userType === 'public'
            ? {
                headerTitle: () => (
                  <View style={{ marginTop: 5 }}>
                    <LogoSerresTessierHorizontal color={headerForegroundColor} height={22} width={180} />
                  </View>
                ),
              }
            : {}),
        },
  );
  return { headerBackgroundColor, headerForegroundColor, progressActiveColor };
};

export interface CredentialsFormFieldsProps {
  control: Control<CredentialsFormSchema>;
  inviteCodeFirst: boolean;
  isLoading: boolean;
  isLoggedIn: boolean;
  possibleActivationCode: boolean;
  refInviteCode: React.RefObject<TextInput | null>;
  refPassword: React.RefObject<TextInput | null>;
  refPassword2: React.RefObject<TextInput | null>;
  requireActivationCode: boolean;
  t: TFunction;
}

export function CredentialsFormFields({
  control,
  inviteCodeFirst,
  isLoading,
  isLoggedIn,
  possibleActivationCode,
  refInviteCode,
  refPassword,
  refPassword2,
  requireActivationCode,
  t,
}: CredentialsFormFieldsProps) {
  return (
    <Column gap="spacing.general.x5">
      {inviteCodeFirst && possibleActivationCode ? (
        <Controller
          name="inviteCode"
          {...{ control }}
          render={({ field, fieldState }) => (
            <Column>
              <Input
                autoCapitalize="characters"
                autoCorrect={false}
                error={fieldState.error}
                innerRef={refInviteCode}
                label={t(requireActivationCode ? 'activationCodeFieldLabel' : 'inviteCodeFieldLabel')}
                labelColor={isWhiteLabel() ? 'text.primary' : 'text.brand'}
                onBlur={field.onBlur}
                onChangeText={field.onChange}
                placeholder={t(requireActivationCode ? 'activationCodeFieldPlaceholder' : 'inviteCodeFieldPlaceholder')}
                style={{
                  color: isWhiteLabel() ? theme.colors['text.primary'] : theme.colors['text.brand'],
                  fontWeight: '700',
                }}
                value={field.value}
              />
              {requireActivationCode && t('activationCodeFieldHelp') !== 'activationCodeFieldHelp' ? (
                <Pressable onPress={() => void safelyOpenURL(t('activationCodeFieldHelpLink'))}>
                  <Text paddingTop="spacing.general.x4" variant="bodyBase">
                    <Trans
                      components={{ a: <Link href={t('activationCodeFieldHelpLink')} /> }}
                      i18nKey="activationCodeFieldHelp"
                      ns="credentials"
                    />
                  </Text>
                </Pressable>
              ) : null}
            </Column>
          )}
        />
      ) : null}

      {!isLoggedIn || isLoading ? (
        <Controller
          name="email"
          {...{ control }}
          render={({ field, fieldState }) => (
            <Input
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect={false}
              error={fieldState.error}
              label={t('emailFieldLabel')}
              onBlur={field.onBlur}
              onChangeText={field.onChange}
              onSubmitEditing={() => refPassword.current?.focus()}
              placeholder={t('emailFieldPlaceholder')}
              returnKeyType="next"
              submitBehavior="submit"
              testID="credentials-email-input"
              value={field.value}
            />
          )}
        />
      ) : null}

      {!isLoggedIn || isLoading ? (
        <Controller
          name="password"
          {...{ control }}
          render={({ field, fieldState }) => (
            <Input
              autoCapitalize="none"
              autoComplete="password-new"
              autoCorrect={false}
              error={fieldState.error}
              innerRef={refPassword}
              label={t('passwordFieldLabel')}
              onBlur={field.onBlur}
              onChangeText={field.onChange}
              onSubmitEditing={() => refPassword2.current?.focus()}
              placeholder={t('passwordFieldPlaceholder')}
              returnKeyType="next"
              secureTextEntry
              submitBehavior="submit"
              testID="credentials-password-input"
              value={field.value}
            />
          )}
        />
      ) : null}

      {!isLoggedIn || isLoading ? (
        <Controller
          name="passwordConfirmation"
          {...{ control }}
          render={({ field, fieldState }) => (
            <Input
              autoCapitalize="none"
              autoComplete="password-new"
              autoCorrect={false}
              error={fieldState.error}
              innerRef={refPassword2}
              label={t('passwordConfirmationFieldLabel')}
              onBlur={field.onBlur}
              onChangeText={field.onChange}
              onSubmitEditing={() => refInviteCode.current?.focus()}
              placeholder={t('passwordConfirmationFieldPlaceholder')}
              returnKeyType="next"
              secureTextEntry
              submitBehavior="submit"
              testID="credentials-password-confirm-input"
              value={field.value}
            />
          )}
        />
      ) : null}

      {!inviteCodeFirst && possibleActivationCode ? (
        <Controller
          name="inviteCode"
          {...{ control }}
          render={({ field, fieldState }) => (
            <Column>
              <Input
                autoCapitalize="characters"
                autoCorrect={false}
                error={fieldState.error}
                innerRef={refInviteCode}
                label={t(requireActivationCode ? 'activationCodeFieldLabel' : 'inviteCodeFieldLabel')}
                onBlur={field.onBlur}
                onChangeText={field.onChange}
                placeholder={t(requireActivationCode ? 'activationCodeFieldPlaceholder' : 'inviteCodeFieldPlaceholder')}
                value={field.value}
              />
              {requireActivationCode && t('activationCodeFieldHelp') !== 'activationCodeFieldHelp' ? (
                <Pressable onPress={() => void safelyOpenURL(t('activationCodeFieldHelpLink'))}>
                  <Text paddingTop="spacing.general.x4" variant="bodyBase">
                    <Trans
                      components={{ a: <Link href={t('activationCodeFieldHelpLink')} /> }}
                      i18nKey="activationCodeFieldHelp"
                      ns="credentials"
                    />
                  </Text>
                </Pressable>
              ) : null}
            </Column>
          )}
        />
      ) : null}
    </Column>
  );
}
